import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { invalidateIdentityQueries } from '../../lib/identityQueryKeys';
import IdentityMappingCsvControls from './IdentityMappingCsvControls';
import IdentityMappingsSection from './IdentityMappingsSection';
import MappingsFrozenBanner from './MappingsFrozenBanner';

// FD-380 (FD-ADR-037 §1) — the identity mapping workspace, as ONE component
// mounted in two places.
//
// Until now this composition lived inline in `MigrationIdentitiesPage`: the
// freeze banner, the CSV controls and the mappings table, assembled in a page
// that only the campaign route could reach. FD-ADR-037 §1 makes
// `Resources › Identities` a write surface too, and the decision it records is
// specifically that there must not be a SECOND editing implementation over the
// same table — the two would diverge exactly where correctness lives (the
// FD-ADR-036 freeze rules, the staged-edit batching, and the key normalisation
// between T2T object-id GUIDs and D2C SIDs).
//
// So the composition moves here and both routes render it. The campaign tab
// supplies its props from the migration record it already holds; Resources
// supplies them from the campaign its selector resolved. Neither owns any
// editing logic of its own.
//
// WHAT DOES NOT MOVE HERE, deliberately: the suggestions feed stays strictly
// per-campaign. `GET /v1/migrations/{id}/identity-suggestions` costs 1–6 Graph
// calls per unique user and is consent-guarded per involved tenant, so the
// global page SELECTS a campaign and never fans Graph work across campaigns
// (FD-ADR-037 §1; the rule FD-221 established and FD-ADR-030 §4 restated).

export interface IdentityWorkspaceProps {
  migrationId: string;
  migrationType: 'T2T' | 'D2C';
  /** The wave BPRT state — drives the FD-ADR-036 freeze presentation. */
  bprtStatus: string | null | undefined;
  /** Assigned device count, used for the empty-state copy. */
  deviceCount: number;
  /**
   * Tenant identity for the add/change surfaces (FD-381). All four come from
   * the migration record, which both mount points already hold.
   */
  sourceTenantDomain?: string | null;
  sourceTenantId?: string | null;
  destTenantDomain?: string | null;
  destTenantId?: string | null;
  /**
   * Where the CSV template/import actions live.
   *
   * `'inline'` renders them as buttons above the queue — the original FD-099
   * shape, and what `Resources › Identities` still uses because it has no
   * overflow menu of its own.
   *
   * `'none'` withholds them because the host already carries them: the campaign
   * route puts them in its `···` menu (`MigrationLayout`), under an
   * "Identity mappings" group header so they read as identity controls rather
   * than campaign ones. Rendering both would give one operator two import
   * buttons with two independent result bars.
   */
  csvControls?: 'inline' | 'none';
}

export default function IdentityWorkspace({
  migrationId,
  migrationType,
  bprtStatus,
  deviceCount,
  sourceTenantDomain,
  sourceTenantId,
  destTenantDomain,
  destTenantId,
  csvControls = 'inline',
}: IdentityWorkspaceProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return (
    <>
      {/* FD-284 — the freeze, said before the operator starts editing rather
          than discovered at submit as a rejection. */}
      <MappingsFrozenBanner bprtStatus={bprtStatus} />
      {csvControls === 'inline' && (
        <IdentityMappingCsvControls
          migrationId={migrationId}
          migrationType={migrationType}
          onImported={() => invalidateIdentityQueries(queryClient, migrationId)}
        />
      )}
      <IdentityMappingsSection
        migrationId={migrationId}
        migrationType={migrationType}
        deviceCount={deviceCount}
        bprtStatus={bprtStatus}
        sourceTenantDomain={sourceTenantDomain}
        sourceTenantId={sourceTenantId}
        destTenantDomain={destTenantDomain}
        destTenantId={destTenantId}
        onAssignDevices={() =>
          navigate(`/migrations/${encodeURIComponent(migrationId)}/devices`)
        }
      />
    </>
  );
}
